/**
 * Inline SVG charts. No chart library.
 *
 * Three shapes cover every chart in Finance and Growth, and each is under a hundred lines,
 * which is less than the configuration a charting library would need to be made to obey the
 * design tokens. Every colour is a CSS variable, so the charts follow a token change the
 * same way the rest of the product does.
 *
 * Accessibility: each chart carries a `role="img"` and a `<title>` that states the same
 * thing the picture does, and every chart is accompanied by the numbers in text elsewhere
 * on the card. Nothing here is the only way to read a value.
 */
import { useId } from 'react';
import { formatMoney } from '@/lib/format';

/* ------------------------------------------------------------------ */
/* Waterfall                                                           */
/* ------------------------------------------------------------------ */

export interface WaterfallBar {
  key: string;
  label: string;
  kind: 'start' | 'deduction' | 'result';
  value_cents: number | null;
  running_cents: number | null;
}

export interface WaterfallChartProps {
  bars: readonly WaterfallBar[];
  height?: number;
}

/**
 * A floating-bar waterfall.
 *
 * The start and the result are grounded columns; each deduction floats between the running
 * total before and after it. That geometry is the whole point: the gap a bar leaves under
 * itself is the money that survived it.
 */
export function WaterfallChart({ bars, height = 180 }: WaterfallChartProps) {
  const titleId = useId();
  const usable = bars.filter((b) => b.value_cents !== null);
  if (usable.length === 0) {
    return <p className="o-muted">No data for this window.</p>;
  }

  const tops = usable.map((b) => Math.max(b.running_cents ?? 0, (b.running_cents ?? 0) - (b.value_cents ?? 0)));
  const max = Math.max(...tops, 1);
  const width = 100;
  const gap = 1.6;
  const barWidth = (width - gap * (usable.length - 1)) / usable.length;
  const scale = (cents: number): number => (cents / max) * height;

  return (
    <figure className="o-chart" role="img" aria-labelledby={titleId}>
      <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" className="o-chart__svg" aria-hidden="true">
        <title id={titleId}>True net waterfall</title>
        {usable.map((bar, index) => {
          const x = index * (barWidth + gap);
          const after = bar.running_cents ?? 0;
          const before = after - (bar.value_cents ?? 0);
          const top = Math.max(after, before);
          const bottom = Math.min(after, before);
          const y = height - scale(top);
          const barHeight = Math.max(1, scale(top) - scale(Math.max(bottom, 0)));
          const fill =
            bar.kind === 'start'
              ? 'var(--burgundy-tint)'
              : bar.kind === 'result'
                ? 'var(--teal)'
                : 'var(--crit)';
          return (
            <rect
              key={bar.key}
              x={x}
              y={y}
              width={barWidth}
              height={barHeight}
              fill={fill}
              opacity={bar.kind === 'deduction' ? 0.85 : 1}
            />
          );
        })}
      </svg>
      <figcaption className="o-chart__legend">
        {usable.map((bar) => (
          <span key={bar.key} className={`o-chart__legend-item o-chart__legend-item--${bar.kind}`}>
            <b>{formatMoney(bar.value_cents, { showCents: false })}</b>
            <span>{bar.label}</span>
          </span>
        ))}
      </figcaption>
    </figure>
  );
}

/* ------------------------------------------------------------------ */
/* Line                                                                */
/* ------------------------------------------------------------------ */

export interface LinePoint {
  label: string;
  value: number | null;
}

export interface LineChartProps {
  points: readonly LinePoint[];
  /** A horizontal rule, for example the cash buffer floor. */
  threshold?: { value: number; label: string } | null;
  /** Vertical markers with a label, for example a PO due date or a tax deadline. */
  markers?: readonly { at: string; label: string; tone?: 'copper' | 'warn' | 'crit' | 'info' }[];
  height?: number;
  title: string;
  format?: (value: number) => string;
}

/**
 * A single line with an optional floor and named vertical markers.
 *
 * The markers are the reason this is not a sparkline: on a cash forecast, "the low point is
 * the 18th" is only useful next to "the purchase order is due on the 15th".
 */
export function LineChart({ points, threshold, markers = [], height = 160, title, format }: LineChartProps) {
  const titleId = useId();
  const values = points.map((p) => p.value).filter((v): v is number => v !== null);
  if (values.length < 2) {
    return <p className="o-muted">Not enough history to draw a line.</p>;
  }

  const width = 100;
  const min = Math.min(...values, threshold?.value ?? Number.POSITIVE_INFINITY);
  const max = Math.max(...values, threshold?.value ?? Number.NEGATIVE_INFINITY);
  const span = max - min || 1;
  const x = (index: number): number => (index / (points.length - 1)) * width;
  const y = (value: number): number => height - ((value - min) / span) * (height - 12) - 6;

  const path = points
    .map((point, index) => (point.value === null ? null : `${index === 0 ? 'M' : 'L'}${x(index).toFixed(2)},${y(point.value).toFixed(2)}`))
    .filter(Boolean)
    .join(' ');

  const markerIndex = (at: string): number => points.findIndex((p) => p.label === at);
  const fmt = format ?? ((value: number) => formatMoney(value, { showCents: false }));

  return (
    <figure className="o-chart" role="img" aria-labelledby={titleId}>
      <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" className="o-chart__svg" aria-hidden="true">
        <title id={titleId}>{title}</title>
        {threshold ? (
          <line
            x1={0}
            x2={width}
            y1={y(threshold.value)}
            y2={y(threshold.value)}
            stroke="var(--warn)"
            strokeWidth={0.4}
            strokeDasharray="2 2"
          />
        ) : null}
        {markers.map((marker) => {
          const index = markerIndex(marker.at);
          if (index < 0) return null;
          return (
            <line
              key={`${marker.at}-${marker.label}`}
              x1={x(index)}
              x2={x(index)}
              y1={0}
              y2={height}
              stroke={`var(--${marker.tone ?? 'copper'})`}
              strokeWidth={0.3}
              strokeDasharray="1.5 1.5"
            />
          );
        })}
        <path d={path} fill="none" stroke="var(--teal)" strokeWidth={0.8} vectorEffect="non-scaling-stroke" />
      </svg>
      <figcaption className="o-chart__legend o-chart__legend--markers">
        {threshold ? <span className="o-chart__marker o-chart__marker--warn">floor {fmt(threshold.value)}</span> : null}
        {markers.map((marker) => (
          <span key={`${marker.at}-${marker.label}`} className={`o-chart__marker o-chart__marker--${marker.tone ?? 'copper'}`}>
            {marker.label}
          </span>
        ))}
      </figcaption>
    </figure>
  );
}

/* ------------------------------------------------------------------ */
/* Stacked bars                                                        */
/* ------------------------------------------------------------------ */

export interface StackedBar {
  label: string;
  segments: { key: string; value: number; tone: string }[];
}

export interface StackedBarChartProps {
  bars: readonly StackedBar[];
  height?: number;
  title: string;
}

/** New versus returning, and any other two-part daily split. */
export function StackedBarChart({ bars, height = 120, title }: StackedBarChartProps) {
  const titleId = useId();
  if (bars.length === 0) return <p className="o-muted">No data for this window.</p>;

  const totals = bars.map((bar) => bar.segments.reduce((sum, s) => sum + s.value, 0));
  const max = Math.max(...totals, 1);
  const width = 100;
  const gap = bars.length > 40 ? 0.2 : 0.8;
  const barWidth = Math.max(0.4, (width - gap * (bars.length - 1)) / bars.length);

  return (
    <figure className="o-chart" role="img" aria-labelledby={titleId}>
      <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" className="o-chart__svg" aria-hidden="true">
        <title id={titleId}>{title}</title>
        {bars.map((bar, index) => {
          let cursor = height;
          return (
            <g key={bar.label}>
              {bar.segments.map((segment) => {
                const segmentHeight = (segment.value / max) * height;
                cursor -= segmentHeight;
                return (
                  <rect
                    key={segment.key}
                    x={index * (barWidth + gap)}
                    y={cursor}
                    width={barWidth}
                    height={Math.max(0, segmentHeight)}
                    fill={`var(--${segment.tone})`}
                  />
                );
              })}
            </g>
          );
        })}
      </svg>
    </figure>
  );
}
